import { EditOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { Empty, Flex, Table, Typography } from "antd";
import { useSelector } from "react-redux";
import { dateValue } from "../../../common/columns";
import type { TaskSummary } from "../../../common/components/CommentsTab";
import { EditTimeEntryModal } from "../../../common/components/EditTimeEntryModal";
import { TableActions } from "../../../common/components/TableActions";
import { formatTotalTime } from "../../../common/utils/formatTotalTime";
import type { TimeEntryGroup } from "../../../common/utils/groupTimeEntries";
import { splitTimeEntries } from "../../../common/utils/groupTimeEntries";
import { buildUserDisplayNameById } from "../../../common/utils/userDisplayName";
import { useTimeEntryEditor } from "../../../common/utils/useTimeEntryEditor";
import type { TimeTracking, UserLimitedList } from "../../generated-axios-client";
import type { RootState } from "../../store";

type Props = {
  intl: IntlShape;
  ticketId: string;
  ticketStatus?: string | null;
  taskMap: Record<string, TaskSummary>;
};

type TaskLogGroup = TimeEntryGroup<TimeTracking>;

export const TaskLogInfo = ({ intl, ticketId, ticketStatus, taskMap }: Props) => {
  const rows = useSelector((state: RootState) => state.ticket.timeSessions);
  const userList = useSelector(
    (state: RootState) => (state.userstate.list.results ?? []) as UserLimitedList[]
  );
  const currentUser = useSelector((state: RootState) => state.userstate.user);
  const editStatus = useSelector((state: RootState) => state.ticket.editTimeEntryStatus);

  const editor = useTimeEntryEditor<TimeTracking>({
    ticketId,
    ticketStatus,
    currentUserId: currentUser?.id,
    currentUserGroups: currentUser?.groups,
    status: editStatus,
  });

  const userMap = buildUserDisplayNameById(userList ?? []);
  const technicianName = (row: TimeTracking) =>
    row.assignee != null ? (userMap[row.assignee] ?? `User ${row.assignee}`) : "—";
  const technicianNames = (group: TaskLogGroup) => group.rows.map(technicianName);

  const taskLabel = (group: TaskLogGroup) => {
    const task = group.entry.ticket_item ? taskMap[group.entry.ticket_item] : undefined;
    if (!task) return "—";
    return task.bladeSerial ? `#${task.number} — ${task.bladeSerial}` : `#${task.number}`;
  };

  const loading = rows === null;
  // Ordered by creation, not by the logged date — the date is editable, so ordering on
  // it would reshuffle the table under the user every time they corrected an entry.
  const taskLogs = loading ? [] : [...splitTimeEntries(rows).taskLogs];
  taskLogs.sort((a, b) => b.sortKey.localeCompare(a.sortKey));

  if (!loading && taskLogs.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={intl.formatMessage({ id: "ticket_task_log_empty" })}
      />
    );
  }

  const columns = [
    {
      key: "index",
      title: "#",
      width: 44,
      render: (_: unknown, __: unknown, i: number) => (
        <Typography.Text type="secondary">{i + 1}</Typography.Text>
      ),
    },
    {
      key: "task",
      title: intl.formatMessage({ id: "ticket_task_log_col_task" }),
      render: (_: unknown, group: TaskLogGroup) => taskLabel(group),
    },
    {
      key: "technicians",
      title: intl.formatMessage({ id: "ticket_session_col_technicians" }),
      render: (_: unknown, group: TaskLogGroup) => (
        <Flex vertical>
          {group.rows.map((row) => (
            <Typography.Text key={row.id}>{technicianName(row)}</Typography.Text>
          ))}
        </Flex>
      ),
    },
    {
      key: "date",
      title: intl.formatMessage({ id: "ticket_task_log_col_date" }),
      render: (_: unknown, group: TaskLogGroup) =>
        group.entry.ended_at ? dateValue(group.entry.ended_at, intl) : "—",
    },
    {
      key: "hours",
      title: intl.formatMessage({ id: "ticket_task_log_col_hours" }),
      render: (_: unknown, group: TaskLogGroup) => formatTotalTime(group.durationSeconds),
    },
    {
      key: "actions",
      title: "",
      width: 56,
      render: (_: unknown, group: TaskLogGroup) =>
        editor.canEdit(group) ? (
          <TableActions
            actions={[
              {
                key: "edit",
                icon: <EditOutlined />,
                tooltip: intl.formatMessage({ id: "ticket_time_edit_tooltip" }),
                onClick: () => editor.openEditor(group),
              },
            ]}
          />
        ) : null,
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={taskLogs}
        rowKey={(group) => group.key}
        loading={loading}
        size="small"
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
      />
      {editor.editing && (
        <EditTimeEntryModal
          open
          mode="task"
          startedAt={editor.editing.entry.started_at}
          endedAt={editor.editing.entry.ended_at}
          title={intl.formatMessage({ id: "ticket_time_edit_title_task" })}
          technicianNames={technicianNames(editor.editing)}
          loading={editor.loading}
          intl={intl}
          onSubmit={editor.submitEdit}
          onCancel={editor.closeEditor}
        />
      )}
    </>
  );
};
